/**
 * Real Shopify Discount & Coupon Code Handler
 * Uses Shopify's /discount/CODE endpoint to apply discounts to the cart session,
 * and updates checkout links and form action attributes dynamically.
 *
 * Rules:
 * 1. Only validates when user clicks "Apply" or presses Enter (NO auto-validation while typing).
 * 2. Applies to Shopify cart session, updates checkout links, and shows green Remove badge.
 * 3. Clicking "Remove" removes discount from Shopify session and resets form.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'lvd_applied_discount';

  function formatMoneySimple(cents) {
    var val = (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var priceEl = document.querySelector('[data-cart-subtotal-price]');
    var symbol = '$';
    if (priceEl) {
      var text = priceEl.textContent.trim();
      var match = text.match(/^[^\d]+/);
      if (match) symbol = match[0];
      else {
        match = text.match(/[^\d]+$/);
        if (match) symbol = ' ' + match[0];
      }
    }
    if (symbol.startsWith(' ')) return val + symbol;
    return symbol + val;
  }

  /** The cart LvdCartLive already read, when it has one — saves a round trip. */
  function knownCart() {
    return (window.LvdCartLive && window.LvdCartLive.cart) || null;
  }

  function readCart(cart) {
    if (cart && typeof cart.items_subtotal_price !== 'undefined') return Promise.resolve(cart);
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (!rootUrl.endsWith('/')) rootUrl += '/';
    return fetch(rootUrl + 'cart.json', { credentials: 'same-origin' }).then(function (res) {
      return res.json();
    });
  }

  // `cart` is optional: pass one to reuse an already-read cart. Existing
  // callers that pass nothing still work.
  window.LvdApplySimulatedDiscount = function(cart) {
    var savedCode = '';
    try {
      savedCode = sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (e) { }

    if (!savedCode) return;
    if (!window.LvdDiscountSimulator) return;

    var simulatedCoupon = window.LvdDiscountSimulator.find(function(c) {
      return c.code.toLowerCase() === savedCode.toLowerCase();
    });

    if (!simulatedCoupon) return;

    readCart(cart)
      .then(function(cart) {
        if (!cart || typeof cart.items_subtotal_price === 'undefined') return;

        var subtotal = cart.items_subtotal_price;
        var discountAmount = 0;
        
        if (simulatedCoupon.type === 'percentage') {
           discountAmount = Math.round(subtotal * (simulatedCoupon.value / 100));
        } else {
           discountAmount = simulatedCoupon.value * 100;
        }

        if (discountAmount > subtotal) discountAmount = subtotal;

        var newTotal = subtotal - discountAmount;

        document.querySelectorAll('[data-cart-subtotal-price]').forEach(function(el) {
          el.innerHTML = formatMoneySimple(newTotal);
        });

        document.querySelectorAll('[data-discounts-list]').forEach(function(ul) {
          ul.innerHTML = '<li><span>' + savedCode.toUpperCase() + '&nbsp;(-' + formatMoneySimple(discountAmount) + ')</span></li>';
        });
      });
  };

  window.LvdRemoveSimulatedDiscount = function(cart) {
    readCart(cart)
      .then(function(cart) {
        if (!cart || typeof cart.items_subtotal_price === 'undefined') return;
        document.querySelectorAll('[data-cart-subtotal-price]').forEach(function(el) {
          el.innerHTML = formatMoneySimple(cart.items_subtotal_price);
        });
        document.querySelectorAll('[data-discounts-list]').forEach(function(ul) {
          ul.innerHTML = '';
        });
      });
  };

  function updateCheckoutUrls(code) {
    var selectors = [
      'a[href*="/checkout"]',
      'button[name="checkout"]',
      'form[action*="/cart"]',
      '.bls-btn-checkout',
      '#cart'
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.tagName === 'FORM') {
          var action = el.getAttribute('action') || '/cart';
          if (code) {
            action = action.split('?')[0] + '?discount=' + encodeURIComponent(code);
          } else {
            action = action.replace(/[?&]discount=[^&]*/g, '');
          }
          el.setAttribute('action', action);
        } else if (el.tagName === 'A') {
          var href = el.getAttribute('href') || '/checkout';
          if (code) {
            href = href.split('?')[0] + '?discount=' + encodeURIComponent(code);
          } else {
            href = href.replace(/[?&]discount=[^&]*/g, '');
          }
          el.setAttribute('href', href);
        }
      });
    });
  }

  /**
   * Apply discount using Shopify's /discount/CODE endpoint.
   * Shopify sets a session cookie. We then fetch the cart JSON to check if it's immediately applicable.
   * We always apply it and let Shopify Checkout perform final validation, preventing valid codes
   * from being blocked when cart requirements (like minimum purchase amounts) aren't met yet.
   */
  function applyDiscount(code, widget) {
    var applyBtn = widget.querySelector('[data-lvd-coupon-apply]');
    var msgEl = widget.querySelector('[data-lvd-coupon-msg]');

    if (!code) {
      if (msgEl) {
        msgEl.style.display = 'block';
        msgEl.className = 'lvd-coupon__msg lvd-coupon__msg--error';
        msgEl.textContent = 'Please enter a discount code.';
      }
      return;
    }

    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.textContent = 'Applying\u2026';
    }
    if (msgEl) msgEl.style.display = 'none';

    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (!rootUrl.endsWith('/')) rootUrl += '/';

    // Step 1: Hit /discount/CODE to apply it to the Shopify session cookie
    fetch(rootUrl + 'discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'follow'
    })
      .then(function () {
        // Step 2: Read the cart to check if it's immediately applicable
        return fetch(rootUrl + 'cart.json', {
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' }
        });
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var isImmediatelyApplicable = false;

        // Check if Shopify confirms the discount is applicable to current line items/total
        if (cart && Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0) {
          cart.discount_codes.forEach(function (dc) {
            if (dc.code && dc.code.toLowerCase() === code.toLowerCase() && dc.applicable) {
              isImmediatelyApplicable = true;
            }
          });
        }

        if (applyBtn) {
          applyBtn.disabled = false;
          applyBtn.textContent = 'Apply';
        }

        // Save code to session and local storage
        try {
          sessionStorage.setItem(STORAGE_KEY, code);
          localStorage.setItem('discount_code', code);
          localStorage.setItem('coupon_applied', 'true');
        } catch (e) { /* ignore */ }

        updateCheckoutUrls(code);
        syncAllWidgets(code);

        if (msgEl) {
          msgEl.style.display = 'block';
          if (isImmediatelyApplicable) {
            msgEl.className = 'lvd-coupon__msg lvd-coupon__msg--success';
            msgEl.textContent = 'Discount code "' + code.toUpperCase() + '" applied!';
          } else {
            msgEl.className = 'lvd-coupon__msg lvd-coupon__msg--success';
            // Custom friendly message for codes requiring minimum spend or specific products
            msgEl.textContent = 'Discount code "' + code.toUpperCase() + '" added! It will be calculated at checkout.';
          }
          setTimeout(function () { msgEl.style.display = 'none'; }, 5000);
        }
      })
      .catch(function () {
        if (applyBtn) {
          applyBtn.disabled = false;
          applyBtn.textContent = 'Apply';
        }

        // Standard fallback to apply it anyway
        try {
          sessionStorage.setItem(STORAGE_KEY, code);
          localStorage.setItem('discount_code', code);
          localStorage.setItem('coupon_applied', 'true');
        } catch (e) { /* ignore */ }

        updateCheckoutUrls(code);
        syncAllWidgets(code);

        if (msgEl) {
          msgEl.style.display = 'block';
          msgEl.className = 'lvd-coupon__msg lvd-coupon__msg--success';
          msgEl.textContent = 'Discount code "' + code.toUpperCase() + '" applied! Verification will happen at checkout.';
          setTimeout(function () { msgEl.style.display = 'none'; }, 5000);
        }
      });
  }

  function removeDiscount(widget) {
    var removeBtn = widget.querySelector('[data-lvd-coupon-remove]');
    var msgEl = widget.querySelector('[data-lvd-coupon-msg]');

    if (removeBtn) {
      removeBtn.disabled = true;
    }

    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (!rootUrl.endsWith('/')) rootUrl += '/';

    // Clear the discount from session by visiting /discount/REMOVE (a non-existent code)
    fetch(rootUrl + 'discount/REMOVE', { credentials: 'same-origin' })
      .catch(function () { /* ignore */ })
      .then(function () {
        if (removeBtn) removeBtn.disabled = false;

        try {
          sessionStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem('discount_code');
          localStorage.removeItem('coupon_applied');
        } catch (e) { /* ignore */ }

        updateCheckoutUrls('');
        syncAllWidgets('');

        if (msgEl) {
          msgEl.style.display = 'block';
          msgEl.className = 'lvd-coupon__msg lvd-coupon__msg--info';
          msgEl.textContent = 'Discount removed.';
          setTimeout(function () { msgEl.style.display = 'none'; }, 3000);
        }
      })
      .catch(function () {
        if (removeBtn) removeBtn.disabled = false;
      });
  }

  function syncWidgetState(widget, code) {
    var formRow = widget.querySelector('[data-lvd-coupon-form]');
    var appliedRow = widget.querySelector('[data-lvd-coupon-applied]');
    var nameEl = widget.querySelector('[data-lvd-coupon-name]');
    var input = widget.querySelector('[data-lvd-coupon-input]');

    if (code) {
      if (formRow) formRow.style.display = 'none';
      if (appliedRow) appliedRow.style.display = 'flex';
      if (nameEl) nameEl.textContent = code.toUpperCase();
    } else {
      if (formRow) formRow.style.display = 'flex';
      if (appliedRow) appliedRow.style.display = 'none';
      if (input) input.value = '';
    }
  }

  function syncAllWidgets(code, cart) {
    var widgets = document.querySelectorAll('[data-lvd-coupon]');
    widgets.forEach(function (w) {
      syncWidgetState(w, code);
    });

    if (code) {
      if (typeof window.LvdApplySimulatedDiscount === 'function') {
        window.LvdApplySimulatedDiscount(cart);
      }
    } else {
      if (typeof window.LvdRemoveSimulatedDiscount === 'function') {
        window.LvdRemoveSimulatedDiscount(cart);
      }
    }
  }

  function getSavedCode() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Repaint every widget from the stored code.
   *
   * The theme re-renders the drawer and the cart summary by swapping innerHTML,
   * which hands us brand new widgets in their default (empty) server state and
   * checkout buttons without the ?discount= param. So this has to be re-runnable
   * and driven by the same signals the cart itself changes on — not a one-shot
   * call at DOMContentLoaded.
   */
  var resyncing = false;
  function resync(cart) {
    if (resyncing) return;
    resyncing = true;
    try {
      var code = getSavedCode();
      syncAllWidgets(code, cart || knownCart());
      updateCheckoutUrls(code);
    } finally {
      resyncing = false;
    }
  }

  // Containers the theme swaps wholesale. Used only to cheaply rule out the
  // vast majority of unrelated DOM churn before doing any real work.
  var CART_ROOTS =
    '#form-mini-cart, .bls-minicart-content, .bls-cart-footer, .cart-wrapper, #main-cart-items, .cart__footer';
  var OURS = '[data-lvd-coupon], .bls-btn-checkout, a[href*="/checkout"], form[action*="/cart"]';

  function watchForReRenders() {
    // 1. Every cart write, wherever it came from (see lvd-shipping-bar.js).
    //    The theme swaps the drawer and summary markup *after* that response
    //    resolves, so run again once the new markup has landed.
    document.addEventListener('lvd:cart:update', function (e) {
      resync(e.detail);
      setTimeout(function () {
        resync(e.detail);
      }, 300);
    });

    // 2. Widgets or checkout buttons injected by a section render. Only react to
    //    added elements that are actually ours, so our own DOM writes (the
    //    discount line, the applied badge) can't feed back into this.
    if (!window.MutationObserver || !document.body) return;
    var pending = null;

    new MutationObserver(function (records) {
      if (resyncing) return;
      var relevant = records.some(function (r) {
        if (r.target.nodeType !== 1 || !r.target.closest(CART_ROOTS)) return false;
        return Array.prototype.some.call(r.addedNodes, function (n) {
          return n.nodeType === 1 && (n.matches(OURS) || n.querySelector(OURS));
        });
      });
      if (!relevant) return;
      clearTimeout(pending);
      pending = setTimeout(resync, 50);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function initCouponHandlers() {
    resync();
    watchForReRenders();

    document.addEventListener('click', function (e) {
      // Apply button
      var applyBtn = e.target.closest('[data-lvd-coupon-apply]');
      if (applyBtn) {
        var widget = applyBtn.closest('[data-lvd-coupon]');
        if (!widget) return;
        var input = widget.querySelector('[data-lvd-coupon-input]');
        var code = input ? input.value.trim() : '';
        applyDiscount(code, widget);
        return;
      }

      // Remove button
      var removeBtn = e.target.closest('[data-lvd-coupon-remove]');
      if (removeBtn) {
        var widget = removeBtn.closest('[data-lvd-coupon]');
        if (!widget) return;
        removeDiscount(widget);
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        var input = e.target.closest('[data-lvd-coupon-input]');
        if (input) {
          e.preventDefault();
          var widget = input.closest('[data-lvd-coupon]');
          if (widget) {
            var applyBtn = widget.querySelector('[data-lvd-coupon-apply]');
            if (applyBtn) applyBtn.click();
          }
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCouponHandlers);
  } else {
    initCouponHandlers();
  }
})();
